/**
 * KPI Progress Entries — reads/writes through the in-memory store.
 *
 * Storage:  src/Data/store.ts (in-memory array)
 * Durable:  PostgreSQL via API write-through
 *
 * NO localStorage reads or writes happen in this file.
 */

import {
  upsertKpiProgressEntry as apiUpsert,
  updateKpiProgressEntryStatus as apiUpdateStatus,
  deleteKpiProgressEntry as apiDelete,
  type CreateKpiProgressEntryRequest,
} from "../../api/kpiProgressEntries";
import {
  kpiProgressStore,
  isProgressHydrated,
} from "../store";

// ── Types ────────────────────────────────────────────────────

export type ProgressEntryStatus = "draft" | "submitted" | "approved" | "rejected" | "cancelled";

export type KpiProgressEntry = {
  entry_guid: string;
  project_guid: string;
  kpi_key?: string; // e.g., "schedule"

  reporting_date: string; // YYYY-MM-DD
  actual_complete: number; // 0..100

  note?: string;
  evidence_url?: string;

  status: ProgressEntryStatus;
  workflow_guid?: string;

  created_by: string;
  created_at: string;
  updated_at: string;
  approved_by?: string;
  approved_at?: string;
};

const EVT = "l4e_kpi_progress_entries_updated";

/** Ensure store is warm (one-time localStorage migration if API hasn't loaded). */
function ensureWarm(): void {
  // No-op: localStorage warm-start removed. Data comes from API hydration only.
}

// ── Reads ────────────────────────────────────────────────────

export function getAllKpiProgressEntries(): KpiProgressEntry[] {
  ensureWarm();
  return kpiProgressStore.slice();
}

export function getKpiProgressEntriesForProject(project_guid: string): KpiProgressEntry[] {
  const pg = String(project_guid || "");
  return getAllKpiProgressEntries()
    .filter((e) => String(e.project_guid) === pg)
    .sort((a, b) => String(b.reporting_date).localeCompare(String(a.reporting_date)));
}

export function getApprovedScheduleProgressForProject(project_guid: string): Array<{
  reporting_date: string;
  actual_complete: number;
}> {
  const pg = String(project_guid || "");
  return getAllKpiProgressEntries()
    .filter((e) => String(e.project_guid) === pg)
    .filter((e) => (e.kpi_key || "schedule") === "schedule")
    .filter((e) => e.status === "approved")
    .map((e) => ({ reporting_date: e.reporting_date, actual_complete: Number(e.actual_complete) }))
    .sort((a, b) => String(a.reporting_date).localeCompare(String(b.reporting_date)));
}

// ── Writes ───────────────────────────────────────────────────

export function saveAllKpiProgressEntries(items: KpiProgressEntry[]) {
  kpiProgressStore.length = 0;
  for (const item of items) {
    kpiProgressStore.push(item);
  }
  try { window.dispatchEvent(new CustomEvent(EVT)); } catch { /* ignore */ }
}

export function upsertKpiProgressEntry(entry: KpiProgressEntry) {
  ensureWarm();
  const eg = String(entry.entry_guid);
  const i = kpiProgressStore.findIndex((e) => String(e.entry_guid) === eg);
  if (i >= 0) {
    kpiProgressStore[i] = { ...kpiProgressStore[i], ...entry };
  } else {
    kpiProgressStore.unshift(entry);
  }

  // Signal React.
  try { window.dispatchEvent(new CustomEvent(EVT)); } catch { /* ignore */ }

  // Write-through to API (fire-and-forget).
  const apiPayload: CreateKpiProgressEntryRequest = {
    entryGuid: entry.entry_guid,
    projectId: entry.project_guid,
    kpiKey: entry.kpi_key || "schedule",
    reportingDate: entry.reporting_date,
    actualComplete: entry.actual_complete,
    note: entry.note,
    evidenceUrl: entry.evidence_url,
    status: entry.status,
    workflowGuid: entry.workflow_guid,
  };
  apiUpsert(apiPayload).catch((err) => {
    console.warn("[progressEntries] API write-through failed:", err?.message || err);
  });
}

export function removeKpiProgressEntry(entry_guid: string) {
  ensureWarm();
  const eg = String(entry_guid || "");
  const i = kpiProgressStore.findIndex((e) => String(e.entry_guid) === eg);
  if (i >= 0) kpiProgressStore.splice(i, 1);

  try { window.dispatchEvent(new CustomEvent(EVT)); } catch { /* ignore */ }

  // Write-through to API.
  apiDelete(eg).catch((err) => {
    console.warn("[progressEntries] API delete failed:", err?.message || err);
  });
}

export function setKpiProgressEntryStatus(args: {
  entry_guid: string;
  status: ProgressEntryStatus;
  by_user_guid?: string;
  approved_at?: string;
}) {
  ensureWarm();
  const eg = String(args.entry_guid || "");
  const i = kpiProgressStore.findIndex((e) => String(e.entry_guid) === eg);
  if (i < 0) return;

  const now = new Date().toISOString();
  const next = { ...kpiProgressStore[i], status: args.status, updated_at: now } as KpiProgressEntry;
  if (args.status === "approved") {
    next.approved_by = String(args.by_user_guid || "");
    next.approved_at = args.approved_at || now;
  }
  kpiProgressStore[i] = next;

  try { window.dispatchEvent(new CustomEvent(EVT)); } catch { /* ignore */ }

  // Write-through to API.
  apiUpdateStatus(eg, args.status, args.by_user_guid).catch((err) => {
    console.warn("[progressEntries] API status update failed:", err?.message || err);
  });
}

// ── Subscription ─────────────────────────────────────────────

export function subscribeKpiProgressEntries(cb: () => void): () => void {
  const onEvt = () => cb();
  window.addEventListener(EVT, onEvt as any);
  return () => {
    window.removeEventListener(EVT, onEvt as any);
  };
}
